import Vue from "vue";
import { session, pendingGuests } from "../services/session";
import { readJson, exchangeHandoff, canAdoptHandoff } from "../../../shared-client/guestSession.mjs";
import Vuex from "vuex";
import api, { apiClient } from "../services/api";
import { serverMessage } from "../utils/memberFunnel";
import colors from "../styles/colors.json";

Vue.use(Vuex);

// Builds the generic Vuex store shared by every app. `config` is the
// consuming app's config module (see src/config.js); the getters read
// config.CREDITS_ENABLED from it.
export function createBaseStore(config) {
  const creditsEnabled = !(config && config.CREDITS_ENABLED === false);
  // One in-flight plan-list request shared by every ensurePlans caller.
  let plansRequest = null;
  return new Vuex.Store({
    state: {
      user: readJson(session, "user"),
      token: session.getItem("token") || null,
      isAuthenticated: !!session.getItem("token"),
      subscription: null,
      plans: [],
      // Public feature flags fetched from GET /public-config at startup.
      // null = not yet loaded; treated as "force-paid off" until loaded.
      publicConfig: null,
      creditBalance: null,
      // Free conversions left, for apps that declare config.FREE_ALLOWANCE.
      freeAllowance: null,
      creditHistory: [],
      accountDataLoading: false,
      accountDataLoaded: false,
      isLoading: false,
      error: null,
      // Theme settings - controlled by colors.json darkMode property
      darkTheme: colors.darkMode !== undefined ? colors.darkMode : true,
      // Global maintenance mode: drives the app-wide banner. Sourced from the
      // public config payload (maintenance_mode flag) so it needs no
      // authenticated endpoint.
      maintenanceMode: false,
      // One in-flight unit of app work the shell keeps watching after the
      // user navigates away, so "it's ready" is never missed. Serializable
      // descriptor set by the app view: { key, statusUrl, statusField,
      // readyStatuses, failStatuses, route, readyText, failText }.
      activeWork: null
    },
    mutations: {
      SET_MAINTENANCE_MODE(state, engaged) {
        state.maintenanceMode = !!engaged;
      },
      SET_ACTIVE_WORK(state, descriptor) {
        state.activeWork = descriptor;
      },
      CLEAR_ACTIVE_WORK(state, key) {
        // A key only clears its own registration, so a newer job's watch
        // survives a stale view's cleanup.
        if (!key || (state.activeWork && state.activeWork.key === key)) {
          state.activeWork = null;
        }
      },
      SET_USER(state, user) {
        // Keys are for explicit integrations, never browser authentication.
        state.user = user;
        // Store user in localStorage for persistence
        session.setItem("user", JSON.stringify(user));
      },
      SET_TOKEN(state, token) {
        state.token = token;
        state.isAuthenticated = !!token;
        // Store token in localStorage for persistence
        if (token) {
          session.setItem("token", token);
        } else {
          session.removeItem("token");
        }
      },
      SET_AUTHENTICATED(state, isAuthenticated) {
        state.isAuthenticated = isAuthenticated;
      },
      LOGOUT(state) {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.subscription = null;
        state.creditBalance = null;
        state.freeAllowance = null;
        state.creditHistory = [];
        state.accountDataLoading = false;
        state.accountDataLoaded = false;
        session.removeItem("user");
        session.removeItem("token");
        session.removeItem("userId");
      },
      SET_SUBSCRIPTION(state, subscription) {
        state.subscription = subscription;
      },
      SET_PLANS(state, plans) {
        state.plans = plans;
      },
      SET_PUBLIC_CONFIG(state, config) {
        state.publicConfig = config;
      },
      SET_CREDIT_BALANCE(state, balance) {
        state.creditBalance = balance;
      },
      SET_FREE_ALLOWANCE(state, value) {
        state.freeAllowance = value;
      },
      SET_CREDIT_HISTORY(state, history) {
        state.creditHistory = history;
      },
      SET_ACCOUNT_DATA_LOADING(state, isLoading) {
        state.accountDataLoading = isLoading;
      },
      SET_ACCOUNT_DATA_LOADED(state, isLoaded) {
        state.accountDataLoaded = isLoaded;
      },
      SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
      },
      SET_ERROR(state, error) {
        state.error = error;
      },
      SET_DARK_THEME(state, isDark) {
        state.darkTheme = isDark;
      }
    },
    actions: {
      // Authentication actions
      async register({ commit }, userData) {
        // A new attempt replaces the last attempt's message (it must not
        // linger on the "Check your email" screen after a successful retry).
        commit("SET_ERROR", null);
        commit("SET_LOADING", true);
        try {
          const response = await api.register(userData);
          // NOTE: registration returns {detail, email[, upgraded]} - not a
          // user object - so it must NOT be committed as SET_USER (doing so
          // used to leave stale half-authenticated state the login guard
          // then had to clear).
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Registration failed"
          );
          console.error("Error during registration:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      // Anonymous trial: acquire a guest session (auto-called by the router
      // guard on meta.allowAnonymous routes when config.ALLOW_ANONYMOUS).
      // Same post-auth bootstrap as login so the rest of the app cannot tell
      // the difference; currentUser.is_anonymous is the only marker.
      async anonymousLogin({ state, commit, dispatch }) {
        const response = await api.anonymousSession(state.token);
        commit("SET_USER", null);
        commit("SET_TOKEN", response.data.access_token);
        await dispatch("fetchCurrentUser");
        await dispatch("initializeAccountState", { refreshUser: false });
        return response.data;
      },

      async login({ commit, dispatch }, { email, password }) {
        commit("SET_ERROR", null);
        commit("SET_LOADING", true);
        try {
          const response = await api.login(email, password);
          commit("SET_USER", null);
          commit("SET_TOKEN", response.data.access_token);

          // After successful login, fetch user data
          await dispatch("fetchCurrentUser");
          await dispatch("initializeAccountState", { refreshUser: false });
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Login failed"
          );
          console.error("Error during login:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      // "Continue with Google": the GIS credential becomes a normal session
      // (the backend signs in, links or creates the per-site account). A
      // signed-in guest's token rides along so the backend upgrades the
      // guest row in place - images and trial history survive - mirroring
      // the register flow's anonymous_token logic. Loading/error state is
      // owned by the button component, not the global form state.
      async loginWithGoogle({ state, commit, dispatch, getters }, { credential, refToken }) {
        const payload = { credential };
        if (refToken) {
          payload.ref_token = refToken;
        }
        const user = getters.currentUser;
        if (state.token && (!user || user.is_anonymous)) {
          payload.anonymous_token = state.token;
        }
        const response = await api.googleLogin(payload);
        commit("SET_USER", null);
        commit("SET_TOKEN", response.data.access_token);
        await dispatch("fetchCurrentUser");
        await dispatch("initializeAccountState", { refreshUser: false });
        return response.data;
      },

      async logout({ commit }) {
        commit("LOGOUT");
        // Redirect to login page will be handled by the component
      },

      async assumeIdentity({ commit, dispatch }, userId) {
        commit("SET_LOADING", true);
        try {
          const response = await api.assumeIdentity(userId);
          commit("SET_USER", null);
          commit("SET_TOKEN", response.data.access_token);
          await dispatch("fetchCurrentUser");
          await dispatch("initializeAccountState", { refreshUser: false });
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to assume identity"
          );
          console.error("Error assuming identity:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async forgotPassword({ commit }, email) {
        commit("SET_LOADING", true);
        try {
          const response = await api.forgotPassword(email);
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to send password reset email"
          );
          console.error("Error during password reset request:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async resetPassword({ commit }, { token, password, passwordConfirm }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.resetPassword(
            token,
            password,
            passwordConfirm
          );
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to reset password"
          );
          console.error("Error during password reset:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async verifyEmail({ commit }, token) {
        commit("SET_LOADING", true);
        try {
          const response = await api.verifyEmail(token);
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to verify email"
          );
          console.error("Error during email verification:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async resendVerificationEmail({ commit }, email) {
        commit("SET_LOADING", true);
        try {
          const response = await api.resendVerificationEmail(email);
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to resend verification email"
          );
          console.error("Error resending verification email:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async fetchCurrentUser({ state, commit, dispatch }) {
        commit("SET_LOADING", true);
        const requestedToken = session.getItem("token");
        try {
          const response = await api.getCurrentUser();
          // A late response from the guest must not replace the member profile.
          const usedToken = response.config && response.config.headers
            ? response.config.headers.Authorization : "Bearer " + requestedToken;
          if (usedToken !== "Bearer " + session.getItem("token")) return state.user;
          commit("SET_USER", response.data);
          dispatch("fetchFreeAllowance");
          return response.data;
        } catch (error) {
          if (session.getItem("token") !== requestedToken && session.getItem("token")) return state.user;
          if (error.response && error.response.status === 401) commit("LOGOUT");
          commit("SET_ERROR", "Unable to verify your session. Please sign in again.");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      // Apps that grant free conversions (config.FREE_ALLOWANCE) report how
      // many are left. Refreshed with the profile and the credit balance.
      async fetchFreeAllowance({ commit }) {
        const setup = config && config.FREE_ALLOWANCE;
        if (!setup || !setup.endpoint) return null;
        try {
          const response = await apiClient.get(setup.endpoint);
          commit("SET_FREE_ALLOWANCE", response.data);
          return response.data;
        } catch (error) {
          return null; // Keep the last value; the header hides the count until it loads.
        }
      },

      async acceptGuestHandoff({ state, commit, dispatch }, incoming) {
        const startingToken = session.getItem("token");
        // Cached profile flags are not proof of guest/member status.
        if (startingToken) await dispatch("fetchCurrentUser");
        const verifiedToken = session.getItem("token");
        if (startingToken && !verifiedToken) throw new Error("Session ended during handoff");
        const result = await exchangeHandoff(config.api.baseURL, config.SITE_SLUG, incoming);
        if (!canAdoptHandoff(verifiedToken, session.getItem("token"), state.user)) {
          pendingGuests.setItem(result.continuation.path, JSON.stringify(result));
        } else {
          commit("SET_USER", null);
          commit("SET_TOKEN", result.access_token);
          await dispatch("fetchCurrentUser");
        }
        return result.continuation;
      },

      async updateProfile({ commit }, userData) {
        commit("SET_LOADING", true);
        try {
          const response = await api.updateProfile(userData);
          commit("SET_USER", response.data);
          return response.data;
        } catch (error) {
          commit(
            "SET_ERROR",
            (error.response &&
              error.response.data &&
              error.response.data.detail) ||
              "Failed to update profile"
          );
          console.error("Error updating profile:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      // Compatibility action now resolves only the authenticated identity.
      async fetchUser({ dispatch }) {
        return dispatch("fetchCurrentUser");
      },

      async initializeAccountState(
        { commit, dispatch, state },
        { refreshUser = true } = {}
      ) {
        if (!state.isAuthenticated && !state.user) {
          commit("SET_ACCOUNT_DATA_LOADED", false);
          return null;
        }

        commit("SET_ACCOUNT_DATA_LOADING", true);

        try {
          let userData = state.user;

          if (refreshUser) {
            userData = state.isAuthenticated
              ? await dispatch("fetchCurrentUser")
              : await dispatch("fetchUser");
          }

          if (!userData) {
            commit("SET_ACCOUNT_DATA_LOADED", false);
            return null;
          }

          await Promise.all([
            dispatch("fetchSubscription"),
            dispatch("fetchCreditBalance")
          ]);

          commit("SET_ACCOUNT_DATA_LOADED", true);
          return userData;
        } catch (error) {
          commit("SET_ACCOUNT_DATA_LOADED", false);
          throw error;
        } finally {
          commit("SET_ACCOUNT_DATA_LOADING", false);
        }
      },
      async fetchSubscription({ commit }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.getCurrentSubscription();
          commit("SET_SUBSCRIPTION", response.data);
          return response.data;
        } catch (error) {
          commit("SET_ERROR", "Failed to fetch subscription data");
          console.error("Error fetching subscription:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      async fetchPlans({ commit }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.getSubscriptionPlans();
          commit("SET_PLANS", response.data);
          return response.data;
        } catch (error) {
          commit("SET_ERROR", "Failed to fetch subscription plans");
          console.error("Error fetching plans:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      // The plan list for surfaces outside the plan pages (the toolbar's plan
      // link): loaded once and quietly, without the page-wide loading flag.
      ensurePlans({ state, commit }) {
        if (state.plans && state.plans.length) return Promise.resolve(state.plans);
        if (!plansRequest) {
          plansRequest = api
            .getSubscriptionPlans()
            .then(response => {
              commit("SET_PLANS", response.data);
              return response.data;
            })
            .catch(() => {
              plansRequest = null;
              return [];
            });
        }
        return plansRequest;
      },
      async fetchPublicConfig({ commit }) {
        // Loads non-secret feature flags (force_paid_plans, ...). Fail-open: if the
        // request fails we default to NOT forcing paid plans so the app stays
        // usable. Unauthenticated endpoint - safe to call before login.
        try {
          const response = await api.getPublicConfig();
          commit("SET_PUBLIC_CONFIG", response.data || {});
          commit(
            "SET_MAINTENANCE_MODE",
            !!(response.data && response.data.maintenance_mode)
          );
          return response.data;
        } catch (error) {
          console.warn("Could not fetch public config:", error);
          commit("SET_PUBLIC_CONFIG", {
            force_paid_plans: false,
            free_tier: {
              mode: "one_time",
              starter_credits: 50,
              refreshes: false
            }
          });
          return null;
        }
      },
      async fetchCreditBalance({ commit, dispatch }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.getCreditBalance();
          commit("SET_CREDIT_BALANCE", response.data);
          dispatch("fetchFreeAllowance");
          return response.data;
        } catch (error) {
          commit("SET_ERROR", "Failed to fetch credit balance");
          console.error("Error fetching credit balance:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      async fetchCreditHistory({ commit }, { limit = 50, offset = 0 } = {}) {
        commit("SET_LOADING", true);
        try {
          const response = await api.getCreditHistory(limit, offset);
          commit("SET_CREDIT_HISTORY", response.data);
          return response.data;
        } catch (error) {
          commit("SET_ERROR", "Failed to fetch credit history");
          console.error("Error fetching credit history:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      async createCheckoutSession(
        { commit },
        { planId, billingCycle, successUrl, cancelUrl }
      ) {
        commit("SET_LOADING", true);
        try {
          const response = await api.createCheckoutSession(
            planId,
            billingCycle,
            successUrl,
            cancelUrl
          );
          return response.data;
        } catch (error) {
          commit("SET_ERROR", serverMessage(error, "Failed to create checkout session"));
          console.error("Error creating checkout session:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      async cancelSubscription({ commit }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.cancelSubscription();
          // Refresh subscription data after cancellation
          await this.dispatch("fetchSubscription");
          return response.data;
        } catch (error) {
          // Pass the error through to the component for handling
          let errorMessage = "Failed to cancel subscription";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            errorMessage = error.response.data.detail;
          }
          commit("SET_ERROR", errorMessage);
          console.error("Error canceling subscription:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async resumeSubscription({ commit }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.resumeSubscription();
          // Refresh subscription data after resuming
          await this.dispatch("fetchSubscription");
          return response.data;
        } catch (error) {
          // Pass the error through to the component for handling
          let errorMessage = "Failed to resume subscription";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            errorMessage = error.response.data.detail;
          }
          commit("SET_ERROR", errorMessage);
          console.error("Error resuming subscription:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },

      async downgradeToFreePlan({ commit }) {
        commit("SET_LOADING", true);
        try {
          const response = await api.downgradeToFreePlan();
          return response.data;
        } catch (error) {
          commit("SET_ERROR", "Failed to downgrade to free plan");
          console.error("Error downgrading to free plan:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      },
      // Accepts a plain credit amount (Billing page) or
      // { amount, successUrl, cancelUrl } (the out-of-credits paywall,
      // which sends the buyer back to their parked conversion).
      async purchaseCredits({ commit }, payload) {
        const amount =
          payload && typeof payload === "object" ? payload.amount : payload;
        const urls =
          payload && typeof payload === "object"
            ? { successUrl: payload.successUrl, cancelUrl: payload.cancelUrl }
            : null;
        commit("SET_LOADING", true);
        try {
          const response = await api.purchaseCredits(amount, urls);
          return response.data;
        } catch (error) {
          commit("SET_ERROR", serverMessage(error, "Failed to purchase credits"));
          console.error("Error purchasing credits:");
          throw error;
        } finally {
          commit("SET_LOADING", false);
        }
      }
    },
    getters: {
      isAuthenticated: state => state.isAuthenticated,
      currentUser: state => state.user,
      token: state => state.token,
      isPlatformAdmin: state => !!(state.user && state.user.role === "admin" && state.user.site_id === 1),
      isAdmin: state => state.user && state.user.role === "admin", // Role-based admin check
      maintenanceMode: state => state.maintenanceMode,
      activeWork: state => state.activeWork,
      currentSubscription: state => state.subscription,
      // Apps with no usable free PLAN (config.HIDE_FREE_PLAN, e.g. a
      // one-free-conversion trial instead) never see the Free card in any
      // plan UI; the DB row stays as platform plumbing.
      availablePlans: state => {
        // Cheapest first everywhere: the API returns DB row order, which is
        // whatever order the plans were seeded in (new tiers land last).
        const plans = (state.plans || []).slice().sort(function (a, b) {
          const priceOf = function (p) {
            const value = Number(p.price_monthly != null ? p.price_monthly : p.price);
            return isNaN(value) ? 0 : value;
          };
          return priceOf(a) - priceOf(b);
        });
        if (!config || !config.HIDE_FREE_PLAN) return plans;
        return plans.filter(function (p) {
          const isFree =
            (p.name && String(p.name).toLowerCase() === "free") ||
            p.price_monthly === 0 ||
            p.price === 0;
          return !isFree;
        });
      },
      // Copilot agent ids exposed by /public-config. {default: 0} means the
      // agent platform is not configured yet - hosts show a placeholder then.
      agentIds: state =>
        (state.publicConfig && state.publicConfig.agent_ids) || { default: 0 },
      // Force-paid experiment master flag (from /public-config).
      forcePaidPlans: state =>
        !!(state.publicConfig && state.publicConfig.force_paid_plans),
      // True when the member's plan is the dearest one on offer, so there is
      // nothing to upgrade to. False while the plan list or the plan is unknown.
      onTopPlan: (state, getters) => {
        const subscription = state.subscription;
        const current = subscription && subscription.subscription_plan
          ? String(subscription.subscription_plan).toLowerCase()
          : "";
        if (!current || current === "free") return false;
        const plans = getters.availablePlans || [];
        const priceOf = function (p) {
          const value = Number(p.price_monthly != null ? p.price_monthly : p.price);
          return isNaN(value) ? 0 : value;
        };
        const mine = plans.find(function (p) {
          return p.name && String(p.name).toLowerCase() === current;
        });
        if (!mine) return false;
        return !plans.some(function (p) {
          return priceOf(p) > priceOf(mine);
        });
      },
      plansLoaded: state => !!(state.plans && state.plans.length),
      // Plans the user can actually purchase. Under force-paid the Free plan is
      // hidden from selection surfaces (pricing page, subscribe gate). Free is
      // detected the same way SubscriptionPlanCard does.
      purchasablePlans: (state, getters) => {
        // Builds on availablePlans so HIDE_FREE_PLAN applies here too.
        const plans = getters.availablePlans || [];
        if (!getters.forcePaidPlans) return plans;
        return plans.filter(function (p) {
          const isFree =
            (p.name && String(p.name).toLowerCase() === "free") ||
            p.price_monthly === 0 ||
            p.price === 0;
          return !isFree;
        });
      },
      // The gate: true when the signed-in user must subscribe before using the
      // app. Only fires once account data is loaded (so we don't false-gate
      // mid-load), when force-paid is on, the user is NOT grandfathered
      // (free_allowed_override), and they are still on the free tier.
      requiresPaidUpgrade: (state, getters) => {
        if (!getters.forcePaidPlans) return false;
        if (!state.accountDataLoaded) return false;
        const user = state.user;
        if (!user) return false;
        if (user.free_allowed_override) return false;
        let plan = "";
        if (state.subscription && state.subscription.subscription_plan) {
          plan = String(state.subscription.subscription_plan).toLowerCase();
        } else if (user.subscription_plan) {
          plan = String(user.subscription_plan).toLowerCase();
        }
        return !plan || plan === "free";
      },
      creditBalance: state => state.creditBalance,
      freeAllowance: state => state.freeAllowance,
      // Purchased (pack) credits the user currently holds. Prefers the credit
      // balance endpoint (polled right after a pack purchase, so changes land
      // within seconds) and falls back to /auth/me's snapshot.
      purchasedCredits: state => {
        let purchased = 0;
        if (state.creditBalance && state.creditBalance.purchased_credits) {
          purchased = Number(state.creditBalance.purchased_credits) || 0;
        }
        if (!purchased && state.user && state.user.purchased_credits) {
          purchased = Number(state.user.purchased_credits) || 0;
        }
        return purchased;
      },
      // One-time free-tier grant, surfaced by public config and the authenticated
      // balance payload so admin changes do not require a frontend rebuild.
      starterCreditGrant: state => {
        const cb = state.creditBalance;
        const authenticatedConfig = cb && cb.free_tier ? cb.free_tier : null;
        const publicConfig =
          state.publicConfig && state.publicConfig.free_tier
            ? state.publicConfig.free_tier
            : null;
        const config = authenticatedConfig || publicConfig || {};
        return {
          amount:
            config.starter_credits != null ? Number(config.starter_credits) : 50,
          awardedAmount:
            cb && cb.starter_grant_amount != null
              ? Number(cb.starter_grant_amount)
              : null,
          awardedAt:
            cb && cb.starter_grant_awarded_at
              ? cb.starter_grant_awarded_at
              : null,
          refreshes: false,
          mode: "one_time"
        };
      },
      // Compatibility alias while components move from the retired daily model.
      freeCredits: (state, getters) => getters.starterCreditGrant,
      // The free user's current spendable balance. Kept under the old getter name
      // temporarily so deployed components remain compatible.
      freeCreditsRemaining: state => {
        const cb = state.creditBalance;
        if (!cb || cb.balance == null) return null;
        return Number(cb.balance);
      },
      // True for free-plan users.
      isFreePlanUser: state => {
        const user = state.user;
        if (!user) return false;
        let plan = user.subscription_plan
          ? String(user.subscription_plan).toLowerCase()
          : "free";
        if (state.subscription && state.subscription.subscription_plan) {
          plan = String(state.subscription.subscription_plan).toLowerCase();
        }
        return !plan || plan === "free";
      },
      // Whether credit numbers should be visible: for every signed-in user,
      // except in apps without credits (config.CREDITS_ENABLED === false).
      showCreditInfo: state => !!state.user && creditsEnabled,
      creditHistory: state => state.creditHistory,
      userSubscription: state => state.subscription,
      accountDataLoading: state => state.accountDataLoading,
      accountDataLoaded: state => state.accountDataLoaded,
      loading: state => state.isLoading,
      error: state => state.error,
      darkTheme: state => state.darkTheme
    }
  });
}

export default createBaseStore;
